import { useState } from 'react'
import { PageHeader, Panel, DataTable, Chip, Button, Modal, Field, FormRow, Textarea, Select, Drawer, Banner, StatTile, useToast, type Column } from '../ds/index'
import { SlotDots } from '../components/Viz'
import { useApi } from '../lib/useApi'
import { post, patch, del } from '../lib/api'
import { num } from '../lib/format'
import type { Group, User } from '../lib/types'
import { LoadingBlock, ErrorBlock } from '../components/Loading'

export default function Groups() {
  const groups = useApi<{ groups: Group[] }>('/api/groups')
  const users = useApi<{ users: User[] }>('/api/users')
  const [adding, setAdding] = useState(false)
  const [selId, setSelId] = useState<string | null>(null)
  const sel = groups.data?.groups.find((g) => g.id === selId) || null
  const reps = (users.data?.users || []).filter((u) => u.role === 'rep' && u.active)
  // A group needs attention when it has no rep, not because nobody has opened it here.
  const unset = (groups.data?.groups || []).filter((g) => g.active && g.reps.length === 0)

  const cols: Column<Group>[] = [
    { key: 'name', header: 'Group', cell: (r) => <><strong className="nowrap" style={r.active ? undefined : { opacity: .55 }}>{r.name}</strong>{!r.active && <> <Chip>not watched</Chip></>}<div className="muted nowrap">{r.fb_ref ? `facebook.com/groups/${r.fb_ref}` : 'no link yet'}</div></>, sortBy: (r) => r.name },
    { key: 'reps', header: 'Reps', cell: (r) => (
      <div className="rep-slots">
        <SlotDots used={r.reps.length} />
        {r.reps.length ? <span className="muted">{r.reps.map((x) => x.name.split(' ')[0]).join(', ')}</span> : r.active ? <Chip tone="warn">no reps</Chip> : <span className="muted">none</span>}
      </div>
    ), sortBy: (r) => r.reps.length },
    { key: 'subs', header: 'Subscribers placed', numeric: true, cell: (r) => num(r.subscribers), sortBy: (r) => r.subscribers },
    { key: 'posts', header: 'Posts, 7 days', numeric: true, cell: (r) => num(r.posts_7d), sortBy: (r) => r.posts_7d },
    { key: 'leads', header: 'Leads, 7 days', numeric: true, cell: (r) => num(r.leads_7d), sortBy: (r) => r.leads_7d },
    { key: 'held', header: 'Held', numeric: true, cell: (r) => r.held ? <Chip tone="warn">{r.held}</Chip> : <span className="muted">0</span>, sortBy: (r) => r.held },
  ]

  return (
    <>
      <PageHeader title="Groups and reps" subtitle="The Facebook groups being watched and the reps in each. Four reps at most per group; the database refuses a fifth."
                  actions={<Button onClick={() => setAdding(true)}>Add group</Button>} />
      {groups.data && (() => {
        const gs = groups.data.groups.filter((g) => g.active)
        return (
          <div className="grid-tiles" style={{ marginBottom: 'var(--s-6)' }}>
            <StatTile focal label="Groups watched" value={gs.length} meta={`${gs.reduce((a, g) => a + g.posts_7d, 0)} posts in 7 days`} />
            <StatTile label="Leads, 7 days" value={gs.reduce((a, g) => a + g.leads_7d, 0)} meta="across every group" />
            <StatTile label="Rep seats filled" value={`${gs.reduce((a, g) => a + g.reps.length, 0)} of ${gs.length * 4}`} meta={`${gs.filter((g) => g.reps.length === 4).length} groups full`} progress={gs.length ? (gs.reduce((a, g) => a + g.reps.length, 0) / (gs.length * 4)) * 100 : 0} />
            <StatTile label="Groups with no rep" value={gs.filter((g) => g.reps.length === 0).length} meta={`${gs.reduce((a, g) => a + g.held, 0)} posts held`} />
          </div>
        )
      })()}
      {unset.length > 0 && (
        <div style={{ marginBottom: 'var(--s-6)' }}>
          <Banner tone="warn" title={`${unset.length} group${unset.length === 1 ? '' : 's'} with no rep`}>
            {unset.map((g) => g.name).join(', ')}. Posts from {unset.length === 1 ? 'it' : 'them'} are held until a rep is in the group. A rep is added automatically when their Muse sends a "Group joined" notice or an alert from the group, or add one here.
          </Banner>
        </div>
      )}
      {groups.error && <ErrorBlock error={groups.error} />}
      <Panel className="panel-flush">
        {!groups.data ? <div style={{ padding: 'var(--s-6)' }}><LoadingBlock /></div> : <DataTable columns={cols} rows={groups.data.groups} rowKey={(r) => r.id} onRowClick={(r) => setSelId(r.id)} />}
      </Panel>
      {adding && <GroupForm onClose={() => setAdding(false)} onSaved={() => { setAdding(false); groups.reload() }} />}
      {sel && <GroupDrawer group={sel} reps={reps} onClose={() => setSelId(null)} onChanged={() => { groups.reload(); users.reload() }} />}
    </>
  )
}

function GroupForm({ group, onClose, onSaved }: { group?: Group; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: group?.name || '', fb_ref: group?.fb_ref || '', notes: group?.notes || '', zone_id: group?.zone_id || '' })
  const [err, setErr] = useState<string | null>(null)
  // A group's home zone: the last resort for where a lead's person is (low confidence).
  const zones = useApi<{ zones: { id: string; name: string; active: boolean }[] }>('/api/zones')
  const save = async () => {
    const body = { name: f.name, fb_ref: f.fb_ref, notes: f.notes, ...(group ? { zone_id: f.zone_id || null } : {}) }
    try { group ? await patch(`/api/groups/${group.id}`, body) : await post('/api/groups', body); onSaved() } catch (e) { setErr((e as Error).message) }
  }
  return (
    <Modal title={group ? 'Edit group' : 'Add group'} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.name.trim()}>Save</Button></>}>
      <div className="stack-tight">
        <Field label="Name, exactly as Facebook shows it" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        <FormRow label="Group link or id" help="Paste the group's facebook.com link. Emails are matched to it by id first, name second."><input className="field" value={f.fb_ref} onChange={(e) => setF({ ...f, fb_ref: e.target.value })} /></FormRow>
        <FormRow label="Notes"><Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></FormRow>
        {group && (zones.data?.zones.length || 0) > 0 && (
          <FormRow label="Home zone" help="Used only when nothing in a lead says where the person is, and only if Zones accepts low confidence.">
            <Select value={f.zone_id} onChange={(e) => setF({ ...f, zone_id: e.target.value })}>
              <option value="">None</option>
              {zones.data!.zones.filter((z) => z.active).map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
            </Select>
          </FormRow>
        )}
        {err && <p className="form-error">{err}</p>}
      </div>
    </Modal>
  )
}

// What adding a rep did to the posts the group was holding.
function addedMessage(r: { released: number; too_old: number }) {
  const parts = [
    r.released ? `${r.released} held post${r.released === 1 ? '' : 's'} sent to screening` : '',
    r.too_old ? `${r.too_old} too old to send (under Not leads)` : '',
  ].filter(Boolean)
  return parts.length ? `Rep added; ${parts.join(', ')}` : 'Rep added'
}

function GroupDrawer({ group, reps, onClose, onChanged }: { group: Group; reps: User[]; onClose: () => void; onChanged: () => void }) {
  const [repId, setRepId] = useState('')
  const [editing, setEditing] = useState(false)
  const toast = useToast()
  const available = reps.filter((r) => !group.reps.some((x) => x.id === r.id))
  const add = async () => {
    try {
      const r = await post<{ released: number; too_old: number }>(`/api/groups/${group.id}/reps`, { rep_id: repId })
      toast(addedMessage(r), 'pos')
      setRepId(''); onChanged()
    } catch (e) { toast((e as Error).message, 'neg') }
  }
  const [busy, setBusy] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const setWatching = async (active: boolean) => {
    setBusy(true)
    try {
      await patch(`/api/groups/${group.id}`, { active })
      toast(active ? `Watching ${group.name} again` : `Stopped watching ${group.name}. It comes back on its own if a rep's alerts from it start again.`, 'pos')
      onChanged()
    } catch (e) { toast((e as Error).message, 'neg') } finally { setBusy(false) }
  }
  const deleteGroup = async () => {
    setBusy(true)
    try { await del(`/api/groups/${group.id}`); toast(`${group.name} is deleted`, 'pos'); onChanged(); onClose() }
    catch (e) { toast((e as Error).message, 'neg'); setConfirmDelete(false) } finally { setBusy(false) }
  }
  const remove = async (id: string) => {
    try { await del(`/api/groups/${group.id}/reps/${id}`); toast('Rep removed', 'pos'); onChanged() } catch (e) { toast((e as Error).message, 'neg') }
  }
  return (
    <Drawer title={group.name} onClose={onClose} footer={<div className="row-tight" style={{ flexWrap: 'wrap' }}>
      <Button variant="secondary" onClick={() => setEditing(true)}>Edit group</Button>
      {group.active
        ? <Button variant="ghost" onClick={() => setWatching(false)} disabled={busy}>Stop watching</Button>
        : <Button variant="secondary" onClick={() => setWatching(true)} disabled={busy}>Watch again</Button>}
    </div>}>
      <div className="stack">
        <div>
          <div className="s-label muted" style={{ marginBottom: 'var(--s-3)' }}>Reps in this group ({group.reps.length} of 4)</div>
          <div className="stack-tight">
            {group.reps.length === 0 && <span className="muted">No reps. Posts from this group are held.</span>}
            {group.reps.map((r) => (
              <div key={r.id} className="spread"><span>{r.name}</span><Button variant="ghost" onClick={() => remove(r.id)}>Remove</Button></div>
            ))}
          </div>
        </div>
        {group.reps.length < 4 ? (
          <div className="row-tight">
            <Select value={repId} onChange={(e) => setRepId(e.target.value)}>
              <option value="">Choose a rep</option>
              {available.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
            </Select>
            <Button onClick={add} disabled={!repId}>Add</Button>
          </div>
        ) : <Banner tone="neutral">This group is full. Remove a rep to add another.</Banner>}
        <span className="muted" style={{ fontSize: 'var(--t-ui)' }}>A rep only gets a lead here when they represent a subscriber that is placed in this group and matches the request.</span>
        {!group.active && (
          <div className="stack-tight" style={{ borderTop: '1px solid var(--rule)', paddingTop: 'var(--s-4)' }}>
            <span className="s-label muted">Not watched</span>
            <span className="muted">Kept for its history. It is watched again on its own when a rep's alert or "Group joined" notice comes from it.</span>
            {confirmDelete ? (
              <div className="stack-tight">
                <span>Delete {group.name} for good? A group with leads on record cannot be deleted; it stays turned off.</span>
                <div className="row-tight"><Button onClick={deleteGroup} disabled={busy}>{busy ? 'Deleting' : 'Yes, delete'}</Button><Button variant="ghost" onClick={() => setConfirmDelete(false)} disabled={busy}>Keep it</Button></div>
              </div>
            ) : <div><Button variant="ghost" onClick={() => setConfirmDelete(true)}>Delete group</Button></div>}
          </div>
        )}
      </div>
      {editing && <GroupForm group={group} onClose={() => setEditing(false)} onSaved={() => { setEditing(false); onChanged() }} />}
    </Drawer>
  )
}
